import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { type GetPositionsForCaseQuery } from "~/graphql/supergraphTypes";
import { ONBOARDING_CASE_TYPES } from "~/pages/Compliance/CaseDetailsDrawer/PaymentInstructions/constants";
import { getInvestingProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { toDbId } from "~/utils/graphql-helpers";
import { type FatcaPositionRow } from "./gridConfig";

export type PositionItem = NonNullable<
  NonNullable<GetPositionsForCaseQuery["complianceProfile"]>["positions"]
>[number];

export type ComplianceCaseItem = NonNullable<
  NonNullable<GetPositionsForCaseQuery["complianceProfile"]>["complianceCases"]
>[number];

export const getProfileRole = (
  queryProfileId: number,
  investingProfileId: number | undefined,
): string => {
  if (queryProfileId === investingProfileId) return "Owner";
  return "Controller";
};

const findCurrentCase = (
  complianceCases: ComplianceCaseItem[],
  currentCaseId: number,
): ComplianceCaseItem | undefined =>
  complianceCases.find((c) => c.complianceCase && c.complianceCase.id === currentCaseId);

export const getCurrentCasePositionGlobalId = (
  complianceCases: ComplianceCaseItem[],
  currentCaseId: number,
): string | undefined =>
  findCurrentCase(complianceCases, currentCaseId)?.complianceCase?.position?.id ?? undefined;

export const getIsCurrentCaseIOType = (
  complianceCases: ComplianceCaseItem[],
  currentCaseId: number,
): boolean => {
  const caseType = findCurrentCase(complianceCases, currentCaseId)?.complianceCase?.caseType;
  return caseType !== undefined && ONBOARDING_CASE_TYPES.includes(caseType);
};

export const getIsCurrentCaseInvestorOnboarding = (
  complianceCases: ComplianceCaseItem[],
  currentCaseId: number,
): boolean =>
  findCurrentCase(complianceCases, currentCaseId)?.complianceCase?.caseType ===
  ComplianceCaseTypeEnum.InvestorOnboarding;

export const buildRowData = (
  positions: PositionItem[],
  complianceCases: ComplianceCaseItem[],
  queryProfileId: number,
  currentCaseId: number,
  options?: { synthesizeCurrentCase?: boolean },
): FatcaPositionRow[] => {
  const currentCasePositionGlobalId = getCurrentCasePositionGlobalId(
    complianceCases,
    currentCaseId,
  );

  const currentCase = findCurrentCase(complianceCases, currentCaseId)?.complianceCase;
  // Onboarding case types (IO/Remediation) may not have their position surfaced through the
  // profile's `positions`: for IO the profile may not yet have a position linked to the case
  // (e.g. early in onboarding when `positions` is empty or `complianceCase.position` is null);
  // for Remediation a position is linked to the case, but until the case is closed it is not
  // considered a linked position through the profile. In those cases, synthesize a row from the
  // current case so the user can still see and assign the document to it. Non-onboarding case
  // types (ProfileRefresh/CIC/DocumentRefresh) always operate on existing positions and don't
  // need this fallback. Callers rendering assignment status pass `synthesizeCurrentCase: false`
  // when the document doesn't reference the current case, so an unassigned case row isn't shown.
  const isCurrentCaseMissing =
    (options?.synthesizeCurrentCase ?? true) &&
    currentCase &&
    getIsCurrentCaseIOType(complianceCases, currentCaseId) &&
    !positions.some((p) => p.onboardingCase && p.id === currentCasePositionGlobalId);
  const effectivePositions: PositionItem[] = isCurrentCaseMissing
    ? [
        {
          id: currentCase.position?.id ?? "",
          arena: null,
          onboardingCase: {
            id: currentCase.id,
            investingProfile: currentCase.investingProfile,
            externalEntity: currentCase.externalEntity,
            program: currentCase.program,
          },
        },
        ...positions,
      ]
    : positions;

  const rows = effectivePositions
    .filter((p) => p.onboardingCase)
    .map((p, index) => {
      const oc = p.onboardingCase!;
      const investingName = getInvestingProfileName(oc.investingProfile) || "";
      const entityName = oc.externalEntity?.name || "";
      return {
        positionName:
          investingName && entityName ? `${investingName} in ${entityName}` : investingName,
        profileRole: getProfileRole(queryProfileId, oc.investingProfile?.id),
        complianceProgram: oc.program?.name || "",
        isCurrentCase:
          (isCurrentCaseMissing && index === 0) || p.id === currentCasePositionGlobalId,
        positionId: p.id ? String(toDbId(p.id)) : undefined,
      };
    });

  return rows.sort((a, b) => Number(b.isCurrentCase) - Number(a.isCurrentCase));
};
